import { Button } from "GameObjects/UI/Button";
import * as Constants from "Data/Constants";
import * as HLF from "html-living-framework";
import * as Topics from "Data/Topics";
import * as SFX from "Data/SoundEffect";
import * as VO from "Data/VoiceOver";
import Settings from "Data/Settings";
import { Howler } from "howler";
import {StartScreenLayout} from "Layouts/StartScreenLayout";
import { AbstractGameState } from "./AbstractGameState";
import SettingsPopup from "Popups/SettingsPopup";
import { Intro } from "./Intro";
import { Background } from "GameObjects/UI/Background";
import { LayoutSprite } from "GameObjects/UI/LayoutSprite";
import { LayoutButton } from "GameObjects/UI/LayoutButton";
import { Application } from "Application";
import { TweenMax } from "gsap";

// @ts-ignore
const AudioContext = window.AudioContext || window.webkitAudioContext;
const inGamesApp = /packageName=org\.pbskids\.gamesapp/.test(window.location.search);

export class StartScreen extends AbstractGameState<typeof StartScreenLayout> implements ReturnType<typeof StartScreenLayout> {
    public static readonly NAME = "StartScreen";
    public readonly layout = StartScreenLayout;

    public readonly bg: LayoutSprite;
    public readonly logo: LayoutSprite;
    public readonly duck: LayoutSprite[];
    public readonly digit: LayoutSprite;
    public readonly matt: LayoutSprite;
    public readonly inez: LayoutSprite;
    public readonly jackie: LayoutSprite;
    public readonly playButton: LayoutButton;
    public readonly setupButton: LayoutButton;

    protected didPlayTitleVO: boolean = false;

    protected readonly backgroundContainer = this.addChildAt(new Background(), 0);
    protected readonly audioDescriptionsButton = new LayoutButton(this.enableAudioDescription.bind(this), VO.PROMPT_AUDIO_DESCRIPTION, SFX.UI_CLICK);

    protected bgm = HLF.Application.instance.audio.getAudioTrack("flyingloop", HLF.AudioCategory.BGM) ?? HLF.Application.instance.audio.createAudioTrack("flyingloop", HLF.AudioCategory.BGM);

    public constructor() {
        super();
        this.bg = new LayoutSprite();
        this.logo = new LayoutSprite();
        this.duck = [
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
        ];
        this.digit = new LayoutSprite();
        this.matt = new LayoutSprite();
        this.jackie = new LayoutSprite();
        this.inez = new LayoutSprite();
        this.playButton = new LayoutButton(()=>this.gotoNextState(Intro.NAME), VO.BUTTON_PLAY_HOVER, SFX.UI_CLICK);
        this.setupButton = new LayoutButton(this.onSettingsButtonPressed.bind(this), VO.BUTTON_SETUP_HOVER, SFX.UI_CLICK);
        this.interactive = true;
        this.addListener(HLF.Input.Events.POINTER_TAP, (event: PIXI.InteractionEvent)=>{
            if (!this.didPlayTitleVO) {
                if (event.target === event.currentTarget) { // aka if the click was not on a button
                    this.playTitleVO();
                    event.stopPropagation();
                }
            }
        });

        this.audioDescriptionsButton.noHover = true;

        this.onKeyDown = this.onKeyDown.bind(this);
        window.addEventListener(HLF.Input.Events.KEY_DOWN, this.onKeyDown, false);
    }

    public init(pSize: PIXI.Point, pData: {name: string}) {
        this.backgroundContainer.addChild(this.bg);
        // draw order
        this.addChild(this.logo);
        this.addChild(...this.duck);
        this.addChild(this.digit);
        this.addChild(this.matt);
        this.addChild(this.jackie);
        this.addChild(this.inez);
        this.addChild(this.playButton);
        this.addChild(this.setupButton);
        this.addChild(this.audioDescriptionsButton);
        super.init(pSize, pData);

        this.homeButton.visible = false;
        this.characterContainer.visible = false;
        this.settingsButton.visible = false;

        if (Settings.moreAnimation === true) {
            TweenMax.staggerFrom([this.logo, ...this.duck, this.matt, this.jackie, this.inez, this.digit], 0.5, {
                pixi: {scale: 0}
            }, 0.05);
        }

        PubSub.publishSync(HLF.SET_KEYBOARD_ENABLED, false);
    }

    public animateIn(pOnComplete: ()=>void) {
        super.animateIn(pOnComplete);
        PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [
            this.audioDescriptionsButton,
            this.playButton,
            this.setupButton,
            this.helpButton,
            this.settingsButton,
            this.homeButton,
        ]);

        if (inGamesApp) { // assume audio is already unlocked in pbs games app
            this.playTitleVO();
        }
        else if (AudioContext) {
            const audioContext = Howler.ctx;
            if (audioContext?.state === "running") { // audio is unlocked and we are on web
                this.playTitleVO();
            }
        }
        else { // browser so old it doesn't have window.AudioContext or window.webkitAudioContext;
            this.playTitleVO();
        }

        if (Settings.audioDescriptionEnabled === false) {
            PubSub.publishSync(HLF.FORCE_FOCUS, this.audioDescriptionsButton);
        }
        else {
            this.audioDescriptionsButton.interactive = false;
        }
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);

        this.audioDescriptionsButton.x = -this.x;
        this.audioDescriptionsButton.y = pSize.y / 2;
        this.audioDescriptionsButton.hitArea = new PIXI.Rectangle(0, 0, (pSize.x / 2) - Math.max(this.playButton.width / 2, this.setupButton.width / 2), pSize.y / 2);
    }

    public gotoNextState(name: string, data?: object) {
        window.removeEventListener(HLF.Input.Events.KEY_DOWN, this.onKeyDown, false);
        PubSub.publishSync(HLF.SET_KEYBOARD_ENABLED, true);
        super.gotoNextState(name, data);
    }

    protected playTitleVO() {
        HLF.Application.instance.voiceover.playVO(VO.TITLE, ()=>{
            this.didPlayTitleVO = true;
        });
        if (Settings.audioDescriptionEnabled) {
            HLF.Application.instance.voiceover.playVO(VO.SPLASH_AUDIO_DESCRIPTION, HLF.PlayMode.Append);
            HLF.Application.instance.voiceover.playVO(VO.SPLASH_AUDIO_DESCRIPTION_TAB, HLF.PlayMode.Append);
        }
        else {
            HLF.Application.instance.voiceover.playVO(VO.PROMPT_AUDIO_DESCRIPTION, HLF.PlayMode.Append);
        }
    }

    protected playHelpVO(): void {
        HLF.Application.instance.voiceover.playVO(VO.SPLASH_HELP);
    }

    protected enableAudioDescription() {
        Settings.audioDescriptionEnabled = true;
        HLF.Application.instance.voiceover.playVO([VO.AD_ON, VO.SPLASH_AUDIO_DESCRIPTION, VO.SPLASH_AUDIO_DESCRIPTION_TAB]);
        this.audioDescriptionsButton.interactive = false;
        PubSub.publishSync(HLF.CLEAR_FOCUS);
    }

    protected onSettingsButtonPressed(): void {
        super.onSettingsButtonPressed();
        Application.instance.analytics.event("select_content", "Set Up");
    }

    protected onSettingsPopupClosed() {
        super.onSettingsPopupClosed();
    }

    private onKeyDown(pEvent: KeyboardEvent): void {
        if (HLF.Input.KeyCode.isEventKey(pEvent, HLF.Input.KeyCodes.ENTER)) {
            pEvent.preventDefault();
            this.enableAudioDescription();
            window.removeEventListener(HLF.Input.Events.KEY_DOWN, this.onKeyDown, false);
            PubSub.publishSync(HLF.SET_KEYBOARD_ENABLED, true);
        }
        else {
            PubSub.publishSync(HLF.GET_KEYBOARD_STATUS, (status: HLF.Input.IKeyboardStatus)=>{
                if (!status.enabled) {
                    PubSub.publishSync(HLF.SET_KEYBOARD_ENABLED, true);
                    pEvent.preventDefault();
                    PubSub.publishSync(HLF.FORCE_FOCUS, this.playButton);
                }
            });
        }
    }
}
